import { FC, useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
// mui components
import { Grid, Button } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
// components
import { NavigationDropdown } from '../navigation-dropdown';
import { ProjectNotification } from '../project-notification';
import ProfilePopperButton from '../popper-button';
import CompaniesButton from '../companies-button';
import ProjectsButton from '../projects-button/index';
import AccountsButton from '../accounts-button/index';
import SettingsButton from '../settings-button';
import AuthButtons from '../auth-buttons';
import { Drafts } from 'components/drafts';
import { HasPermission } from 'components/has-permission';
import AppPrompt from 'components/app-prompt';
import ProjectFileCabinetModal from 'components/file-cabinet-folders/modals/project-file-cabinet-modal';
// icons
import HomeIcon from 'assets/icons/header/home.svg';
import BuilderPadLogo from 'assets/icons/header/logo-builder-pad.svg';
import FileCabinetIcon from 'assets/icons/cabinet/folder-open.svg';
// api
import {
  useGetProfilePreferenceQuery,
  DonwloadMobileAppEnum,
} from 'redux-store/api/profile-preference';
import { ICurrentPaymentPlan } from 'redux-store/api/billing';
import { IRole, IUserMe } from 'redux-store/types';
import { ICompanyAll, useGetCompanyQuery } from 'redux-store/api/company';
import { useGetProjectByIdQuery } from 'redux-store/api/projects';
import { handleSetBreadCrumbs, handleSetSearchValue } from 'redux-store/slices/file-cabinet-slice';
import { useAppDispatch } from 'redux-store/hooks/useAppDispatch';
import {
  PERMISSIONS_SUBJECT_ENUM,
  useGetCompanyRolePermissionsMeQuery,
} from 'redux-store/api/company-roles';
// hooks
import { useHeaderState } from '../useHeaderState';
//styles
import { headerStyles } from '../styles/styles';
//utils
import { isBrowser } from 'react-device-detect';

interface IHeaderNewUIProps {
  isShowBillingInfo: boolean;
  currentRole: IRole;
  currentPaymentPlan: ICurrentPaymentPlan;
  companyData: ICompanyAll[];
  anchorRef: React.MutableRefObject<HTMLButtonElement>;
  userMe: IUserMe;
}

const HeaderNewUI: FC<IHeaderNewUIProps> = ({
  isShowBillingInfo,
  currentRole,
  currentPaymentPlan,
  companyData,
  anchorRef,
  userMe,
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { hash } = location;

  const [isOpenFileCabinetModal, setIsOpenFileCabinetModal] = useState(false);
  const { showHeaderActionButtons, showLogo, showProfileButtons } = useHeaderState({ userMe });
  const projectId = Number(window.location.pathname.split('/')?.[2]);
  const isSmallScreen = useMediaQuery('(max-width: 850px)');

  const { data: userPermissions } = useGetCompanyRolePermissionsMeQuery();

  const { data: company } = useGetCompanyQuery(undefined, {
    skip: !userPermissions?.COMPANY_MANAGEMENT?.canRead,
  });

  const { data: preference } = useGetProfilePreferenceQuery();
  const { data: project } = useGetProjectByIdQuery(
    { id: projectId },
    { skip: !(projectId && window.location.pathname.includes('project-dashboard')) }
  );

  const props = { isShowBillingInfo, currentRole, currentPaymentPlan };

  const handleResetBreadCrumbs = useCallback(() => {
    if (location.pathname.includes('file-cabinet')) {
      dispatch(handleSetBreadCrumbs([]));
      localStorage.removeItem('fileCabinetBreadCrumbs');
    }
  }, [location]);

  useEffect(() => {
    if (!isOpenFileCabinetModal) {
      if (!!hash) {
        navigate(window.location.pathname + window.location.search, { replace: true });
      }
    }
  }, []);

  const handleOpenFileCabinet = () => {
    localStorage.setItem('isFileCabinetModalOpen', 'true');
    setIsOpenFileCabinetModal(true);
  };

  const handleCloseFileCabinetModal = useCallback(() => {
    setIsOpenFileCabinetModal(false);
    navigate(`${location.pathname}`);
    dispatch(handleSetBreadCrumbs([]));
    dispatch(handleSetSearchValue(null));
    localStorage.removeItem('fileCabinetBreadCrumbs');
    localStorage.removeItem('isFileCabinetModalOpen');
  }, [location]);

  return (
    <Grid
      container
      component="header"
      flexDirection="row"
      alignItems="center"
      justifyContent="space-between"
      sx={headerStyles.header}
    >
      <Grid sx={headerStyles.buttonsContainer}>
        {showLogo && (
          <Grid item sx={headerStyles.logoContainer}>
            <Link to={!!userMe ? '/account-dashboard' : '/sign-in'}>
              {company?.logo ? <img src={company.logo} /> : <BuilderPadLogo />}
            </Link>
          </Grid>
        )}

        {!showLogo && (
          <Grid item sx={headerStyles.logoContainerHome}>
            <Link to="/account-dashboard" onClick={handleResetBreadCrumbs}>
              <HomeIcon />
            </Link>
          </Grid>
        )}

        {companyData.length > 1 && <CompaniesButton companyData={companyData} userMe={userMe} />}

        {showHeaderActionButtons && (
          <>
            <ProjectsButton />
            <HasPermission
              type="multiple"
              permissionRules={[
                { subject: PERMISSIONS_SUBJECT_ENUM.SELECTION_MANAGE, scopes: ['canRead'] },
                { subject: PERMISSIONS_SUBJECT_ENUM.VIEW_PROJECT_FOLDERS, scopes: ['canRead'] },
              ]}
            >
              <NavigationDropdown projectId={projectId} />
            </HasPermission>
            <AccountsButton project={project} />
            <SettingsButton />
          </>
        )}
      </Grid>

      <Grid item sx={headerStyles.popperButtonContainer}>
        <Drafts />
        <Grid sx={headerStyles.actionButtons}>
          {showHeaderActionButtons && (
            <>
              <HasPermission
                type="single"
                subject={PERMISSIONS_SUBJECT_ENUM.VIEW_PROJECT_FOLDERS}
                scope="canRead"
              >
                <Grid sx={headerStyles.buttonContainer}>
                  <Button
                    ref={anchorRef}
                    variant="outlined"
                    startIcon={<FileCabinetIcon />}
                    onClick={handleOpenFileCabinet}
                    sx={headerStyles.fileCabinetButton}
                  />
                </Grid>
              </HasPermission>
              <Grid sx={headerStyles.projectNotifications(isSmallScreen)}>
                <ProjectNotification projectId={projectId} />
              </Grid>
            </>
          )}
        </Grid>

        {showProfileButtons ? <ProfilePopperButton {...props} /> : <AuthButtons />}
      </Grid>

      {preference?.downloadMobileApp === DonwloadMobileAppEnum.OFFER_DOWNLOAD && isBrowser && (
        <AppPrompt anchorRef={anchorRef} isProgressTrackOpen={preference?.isProgressTrackOpen} />
      )}

      {isOpenFileCabinetModal && (
        <ProjectFileCabinetModal
          open={isOpenFileCabinetModal}
          handleClose={handleCloseFileCabinetModal}
          title={project?.name}
          projectId={projectId}
          projectFolderId={project?.folderId}
        />
      )}
    </Grid>
  );
};

export default HeaderNewUI;
